import type {
  Paginated,
  PayoutBulkCreateRequest,
  PayoutBulkResult,
  PayoutCancelRequest,
  PayoutCreateRequest,
  PayoutListItem,
  PayoutMarkPaidRequest,
  PayoutReadyItem,
  PayoutStatus,
  PayoutSummary,
} from '@ayub/shared'
import { apiGet, apiPost } from '@/lib/api'

/** `docs/04-ui-ux.md` "Jadval (List ko'rinish)" — sahifada 50 ta, xuddi blogerlar kabi. */
export const PAYOUTS_PAGE_SIZE = 50

export interface PayoutListQuery {
  status?: PayoutStatus
  page?: number
  pageSize?: number
}

/**
 * `GET /api/payouts` javobi.
 *
 * Hisoblagichlar TEKIS joylashadi (ichma-ich `summary` obyekti emas) — bu
 * `components/bloggers/api.ts` va `components/videos/api.ts` dagi naqshning aynan o'zi
 * (`stageCounts`, `quickCounts` ham shunday). Server `Paginated<PayoutListItem> &
 * PayoutSummary` qaytaradi.
 *
 * Dastlab bu yerda ichma-ich `summary` maydoni taxmin qilingan edi va integratsiyada
 * to'g'rilandi: `data.summary` har doim `undefined` bo'lib, hisoblagichlar bo'sh chiqardi.
 */
export type PayoutListResponse = Paginated<PayoutListItem> & PayoutSummary

/** `GET /api/payouts/:id/card` va `GET /api/bloggers/:id/card` javobi — ikkalasi ham bir xil shakl. */
export interface PayoutCardResponse {
  cardNumber: string
  cardHolder: string | null
}

function buildQueryString(query: PayoutListQuery): string {
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  const qs = params.toString()
  return qs.length > 0 ? `?${qs}` : ''
}

export function fetchPayouts(query: PayoutListQuery): Promise<PayoutListResponse> {
  return apiGet<PayoutListResponse>(`/payouts${buildQueryString(query)}`)
}

/**
 * "To'lovga tayyor" ro'yxati — `PayoutReadyItem[]`, sahifalanmagan (`packages/shared/src/
 * api.ts`: "Bu SAQLANMAYDI — har safar hisoblanadi"). Ro'yxat odatda kichik (bir nechta o'nlab
 * bloger), shuning uchun to'liq massiv bir so'rovda keladi.
 */
export function fetchReadyPayouts(): Promise<PayoutReadyItem[]> {
  return apiGet<PayoutReadyItem[]>('/payouts/ready')
}

/**
 * Muayyan to'lov yozuvining TO'LIQ karta raqami — auditlanadigan endpoint. Faqat admin 👁
 * bosganda chaqiriladi, hech qachon oldindan yuklanmaydi (vazifa talabi).
 *
 * `PayoutListItem.cardNumberMasked` — to'lov yaratilgan paytda NUSXALANGAN karta (bloger
 * keyin o'zgartirsa ham tarixda saqlanadi, `packages/shared/src/api.ts` izohi). Shuning uchun
 * "So'rovlar" va "Tarix" tablarida karta shu (`/payouts/:id/card`) orqali ochiladi — bloger
 * kartasi emas, AYNAN o'sha to'lovga tegishli nusxa.
 */
export function fetchPayoutCard(id: number): Promise<PayoutCardResponse> {
  return apiGet<PayoutCardResponse>(`/payouts/${id}/card`)
}

/**
 * "To'lovga tayyor" tabidagi qatorlar (`PayoutReadyItem`) SAQLANMAGAN — ularda to'lov `id`si
 * yo'q, faqat `bloggerId`. Shuning uchun bu yerda karta AYNAN Bosqich 6da qurilgan, HAQIQIY
 * ishlayotgan endpoint orqali ochiladi (`apps/server/src/api/routes/bloggers.ts`:
 * `GET /api/bloggers/:id/card`, `BloggerDetail.tsx`dagi bilan bir xil naqsh) — bu taxmin EMAS.
 */
export function fetchBloggerCardForPayout(bloggerId: number): Promise<PayoutCardResponse> {
  return apiGet<PayoutCardResponse>(`/bloggers/${bloggerId}/card`)
}

/**
 * Bitta to'lov yaratish — kontrakt (`PayoutCreateRequest`) FIKS. `docs/06-implementation-
 * plan.md`: "Ikki bosqichli to'lov: POST /api/payouts (REQUESTED, videolar bog'lanmaydi) →
 * /paid (tranzaksiya ichida...)" — ya'ni bu chaqiruv o'zi hali PUL O'TKAZILDI degani emas,
 * faqat so'rov yozuvini ochadi. "To'lovga tayyor" tabidagi bitta qatorni to'lash shu funksiya
 * bilan `markPayoutPaid()`ni ZANJIRLAB chaqiradi (`PayoutReadyView.tsx`) — admin uchun BITTA
 * tugma, ichkarida ikkita so'rov.
 */
export function createPayout(body: PayoutCreateRequest): Promise<PayoutListItem> {
  return apiPost<PayoutListItem>('/payouts', body)
}

/**
 * Ommaviy to'lov — kontrakt (`PayoutBulkCreateRequest`/`PayoutBulkResult`) FIKS.
 *
 * ⚠️ TAXMIN: yagona so'rov bilan tanlangan blogerlar UCHUN TO'LIQ yakunlangan (ikki bosqich
 * server ichida atomik bajarilgan) natija qaytaradi deb faraz qilingan — vazifa ta'rifidagi
 * namuna matn shuni ko'rsatadi ("45 ta TO'LANDI, 3 tasi o'tkazib yuborildi", "so'rov
 * yaratildi" emas). Bir nechta o'nlab-yuzlab bloger uchun har biriga ikkita HTTP so'rov
 * yuborish o'rniga bu ancha ishonchli yechim.
 */
export function createBulkPayout(body: PayoutBulkCreateRequest): Promise<PayoutBulkResult> {
  return apiPost<PayoutBulkResult>('/payouts/bulk', body)
}

/**
 * Mavjud so'rovni "To'landi" deb belgilash — kontrakt (`PayoutMarkPaidRequest`) FIKS.
 * Bu — pulni HAQIQATDA o'tkazgandan keyin bosiladigan, qaytarib bo'lmaydigan amal
 * (`docs/03-app-flow.md` "To'ladim" bosilganda): blogerning balansi 0 ga tushadi va
 * bog'liq videolar `To'landi` ga o'tadi.
 */
export function markPayoutPaid(id: number, body: PayoutMarkPaidRequest): Promise<PayoutListItem> {
  return apiPost<PayoutListItem>(`/payouts/${id}/paid`, body)
}

/** So'rovni bekor qilish — kontrakt (`PayoutCancelRequest`) FIKS, `note` majburiy (blogerga ko'rsatiladi). */
export function cancelPayout(id: number, body: PayoutCancelRequest): Promise<PayoutListItem> {
  return apiPost<PayoutListItem>(`/payouts/${id}/cancel`, body)
}

/**
 * "[💳 Karta so'rash]" — `docs/03-app-flow.md`: "bot orqali blogerga karta so'rovini
 * yuboradi... javob bermaganlar uchun".
 *
 * Endpoint nomi `docs/05-backend-schema.md` API ro'yxatidan olingan. Dastlab bu yerda
 * `/request-card` deb taxmin qilingan edi va integratsiyada to'g'rilandi — serverda
 * bunday yo'l umuman yo'q edi, tugma 404 olardi.
 *
 * `sent` — nechta xabar yetkazilgani. Bloger botni bloklagan bo'lsa `0` qaytadi,
 * lekin so'rovning o'zi xato hisoblanmaydi.
 */
export function requestBloggerCard(bloggerId: number): Promise<{ sent: number }> {
  return apiPost<{ sent: number }>(`/bloggers/${bloggerId}/ask-card`)
}
